import { useMemo } from 'react'

import useFeatureFlags, { type UseFeatureFlags } from 'hooks/useFeatureFlags'

import { VirtualCardType } from './constants'

const WEX_FLAG_KEY = 'admin.virtual-card.use-wex-vcn'
const MARQETA_FLAG_KEY = 'admin.virtual-card.use-marqeta-vcn'

/** Every provider a new card can be issued on, in display order. */
export const VCN_PROVIDERS = [VirtualCardType.WEX, VirtualCardType.MARQETA]

export interface VcnProviderAvailability {
  isWexEnabled: boolean
  isMarqetaEnabled: boolean
  /** False only when BOTH providers are switched off — no card can be created. */
  hasAnyProvider: boolean
  isProviderEnabled: (provider: VirtualCardType) => boolean
  /** Selectable providers, display order — for option lists and copy. */
  enabledProviders: VirtualCardType[]
  /** Switched-off providers, display order — for "X is unavailable" copy. */
  disabledProviders: VirtualCardType[]
}

/** Shown next to a switched-off provider so admins know it is deliberate and temporary. */
export const PROVIDER_DISABLED_REASON = 'Temporarily unavailable'

/** Enum values are shouty tokens ('MARQETA'); these are the names admins read. */
export const VCN_PROVIDER_NAMES: Record<VirtualCardType, string> = {
  [VirtualCardType.WEX]: 'WEX',
  [VirtualCardType.MARQETA]: 'Marqeta',
}

/** Option copy for every provider picker — each one carries the same guidance. */
export const VCN_PROVIDER_OPTION_LABELS: Record<VirtualCardType, string> = {
  [VirtualCardType.WEX]: 'WEX (default)',
  [VirtualCardType.MARQETA]: 'Marqeta — only if carrier does not accept WEX',
}

/**
 * Option copy for a picker. The guidance only helps while there is a choice to
 * guide — with a single provider left, "only if the carrier does not accept
 * WEX" points at an option the admin cannot see, so show the bare name.
 */
export const providerOptionLabel = (
  provider: VirtualCardType,
  enabledProviders: VirtualCardType[]
) =>
  enabledProviders.length > 1
    ? VCN_PROVIDER_OPTION_LABELS[provider]
    : VCN_PROVIDER_NAMES[provider]

/**
 * "Marqeta is temporarily unavailable" — no trailing period, so callers can end
 * the sentence or keep going ("… Row(s) 4 ask for a new card on it.").
 */
export const describeDisabledProviders = (
  disabledProviders: VirtualCardType[]
) => {
  const names = disabledProviders.map(
    (provider) => VCN_PROVIDER_NAMES[provider]
  )
  return `${names.join(' and ')} ${names.length > 1 ? 'are' : 'is'} ${PROVIDER_DISABLED_REASON.toLowerCase()}`
}

/**
 * Per-provider safety switches (EMS-9720). Ops flips a provider off during an
 * outage to stop NEW card creation; terminate, reuse and existing cards are
 * never gated. Both flags default to true — LaunchDarkly being unreachable is
 * not a provider outage.
 *
 * Note: `use-wex-vcn` is a reused key. It previously routed creates between
 * providers; it now means "WEX may be selected".
 */
export function useVcnProviderAvailability(): VcnProviderAvailability {
  const { flags } = useFeatureFlags()
  const isWexEnabled = flags?.[WEX_FLAG_KEY] !== false
  const isMarqetaEnabled = flags?.[MARQETA_FLAG_KEY] !== false
  // Stable while the flags hold, so callers can put this in useCallback deps
  // and still see a switch flip — a fresh object every render would either
  // defeat the memo or leave the handler holding a stale snapshot.
  return useMemo(
    () => buildAvailability(isWexEnabled, isMarqetaEnabled),
    [isWexEnabled, isMarqetaEnabled]
  )
}

/**
 * Same rules for callers that cannot use the hook — class components read the
 * flags from props (see ActionsContainer's withFeatureFlags wrapper).
 */
export function getVcnProviderAvailability(
  flags: Partial<UseFeatureFlags['flags']> | undefined
): VcnProviderAvailability {
  return buildAvailability(
    flags?.[WEX_FLAG_KEY] !== false,
    flags?.[MARQETA_FLAG_KEY] !== false
  )
}

function buildAvailability(
  isWexEnabled: boolean,
  isMarqetaEnabled: boolean
): VcnProviderAvailability {
  const isProviderEnabled = (provider: VirtualCardType) =>
    provider === VirtualCardType.MARQETA ? isMarqetaEnabled : isWexEnabled
  return {
    isWexEnabled,
    isMarqetaEnabled,
    hasAnyProvider: isWexEnabled || isMarqetaEnabled,
    isProviderEnabled,
    enabledProviders: VCN_PROVIDERS.filter((provider) =>
      isProviderEnabled(provider)
    ),
    disabledProviders: VCN_PROVIDERS.filter(
      (provider) => !isProviderEnabled(provider)
    ),
  }
}
